import { useSelector } from 'react-redux';
import { useProducts } from '/@/hooks/recommendation';
import { getCropConfig } from '/@/store/private-data/recommendations/selectors';
import { getActiveRecommendation } from '/@/store/recommendations/selectors';
import { getSessionBrands } from '/@/store/session/selectors';
import type { Field } from '/@/types/field';
import type { Hybrid } from './hybrid-card';

/**
 * Hybrids ranked for the field's current crop config.
 *
 * Queries `useProducts` directly rather than going through `useRecommendationEditData`: that
 * hook owns the defaulting cascade, and one of its effects rewrites `cropConfig.environment`
 * on mount — a second copy here would discard the environment picked on the previous step.
 */
export const useHybrids = ({
    field,
    crop = 'corn',
}: {
    field?: Field;
    crop?: 'corn' | 'soy';
}): { featuredProducts: Hybrid[]; otherProducts: Hybrid[]; isLoading: boolean } => {
    const recommendation = useSelector(getActiveRecommendation);
    const cropConfig = getCropConfig(recommendation, crop);
    const sessionBrands = useSelector(getSessionBrands);
    const brandCodename = sessionBrands?.[crop]?.[0]?.codename;

    const productsQuery = useProducts(
        // `useProducts` declares these as strings, but its callers all pass a field's numeric
        // coordinates and environment; they only ever end up in a query string.
        {
            lat: field?.lat,
            lng: field?.lng,
            crop: cropConfig?.crop,
            plantingDate: cropConfig?.plantingDate,
            brandCodename,
            environment: cropConfig?.environment,
        } as unknown as Parameters<typeof useProducts>[0],
        { enabled: !!brandCodename && !!cropConfig?.environment }
    );

    return {
        featuredProducts: productsQuery.data?.featuredProducts || [],
        otherProducts: productsQuery.data?.otherProducts || [],
        isLoading: productsQuery.isLoading || (productsQuery.isValidating && !productsQuery.data),
    };
};
